import { ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '@/app/router/routes';
import { GLOBAL_PAGES, moduleDashboardPath, resolveLocation } from '@/app/router/modules';

interface Crumb {
  label: string;
  /** Absent on the current page. */
  to?: string;
}

/**
 * Dashboard / Module / Page — derived from the School OS registry, so the
 * trail always agrees with the launcher and the module dashboard that led
 * here. A record opened from a list adds a plain "Detail": its name lives in
 * the page's own header, and fetching it again for the trail would make the
 * shell issue queries of its own.
 */
export function Breadcrumb() {
  const { pathname } = useLocation();
  const crumbs = trail(pathname);

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted">
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <li
              key={`${crumb.label}-${index}`}
              // On a phone only the last two steps fit; the rest stay in the markup.
              className={`flex min-w-0 items-center gap-1.5 ${index < crumbs.length - 2 ? 'hidden sm:flex' : ''}`}
            >
              {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden="true" />}
              {last || crumb.to === undefined ? (
                <span aria-current={last ? 'page' : undefined} className={`truncate ${last ? 'font-medium text-ink' : ''}`}>
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.to} className="whitespace-nowrap rounded hover:text-ink focus-visible:outline-2 focus-visible:outline-[var(--sms-accent-ring)]">
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function trail(pathname: string): Crumb[] {
  const home: Crumb = { label: 'Dashboard', to: ROUTES.dashboard };
  if (pathname === ROUTES.dashboard) {
    return [{ label: 'Dashboard' }];
  }

  const location = resolveLocation(pathname);
  if (location !== null) {
    const { module, sub, isDetail } = location;
    const crumbs: Crumb[] = [home, { label: module.title, to: moduleDashboardPath(module.key) }];
    // A module with a single page named like itself ("Examinations") gets one crumb, not two.
    if (sub !== undefined && sub.title !== module.title) {
      crumbs.push({ label: sub.title, to: sub.path });
    } else if (sub !== undefined && isDetail) {
      crumbs[1] = { label: module.title, to: sub.path };
    }
    if (isDetail) {
      crumbs.push({ label: 'Detail' });
    }
    return crumbs;
  }

  const page = GLOBAL_PAGES.find((candidate) => pathname === candidate.path || pathname.startsWith(`${candidate.path}/`));
  if (page !== undefined) {
    return [home, { label: page.title }];
  }
  return [home];
}
